<template>
    <div class="dashboard-layout">
        <!-- Navigation Drawer -->
        <v-navigation-drawer
            v-if="is_logged_in"
            v-model="drawer"
            clipped
            app
            color="background"
        >
            <v-list
                class="d-flex flex-column py-0"
                style="height: 100%"
            >
                <!-- Logo i tytuł aplikacji -->
                <v-list-item
                    class="dashboard-layout__logo"
                    @click="typed_router.push({ name: 'dashboard' })"
                >
                    <img
                        src="@/assets/img/logo_pravna.svg"
                        alt="Logo aplikacji Pravna"
                    />
                </v-list-item>
                <v-divider />

                <!-- Menu nawigacyjne -->
                <v-list
                    nav
                    color="primary"
                >
                    <template
                        v-for="(item, i) in filtered_nav_items"
                        :key="i"
                    >
                        <v-list-item
                            v-if="item.type === 'link'"
                            :to="item.to"
                            :prepend-icon="item.icon"
                            :title="item.title"
                            :class="{ 'v-list-item--active': isActive(item.to!.name) }"
                        />

                        <template v-else-if="item.type === 'group'">
                            <v-list-item
                                :title="item.title"
                                disabled
                                style="opacity: 0.6"
                            />
                            <v-list-item
                                v-for="(sub, j) in item.children"
                                :key="j"
                                :to="sub.to"
                                :prepend-icon="sub.icon"
                                :title="sub.title"
                                :class="{ 'v-list-item--active': isActive(sub.to.name) }"
                            />
                        </template>
                    </template>
                </v-list>

                <!-- Stopka -->
                <v-spacer />

                <v-divider />
                <v-list-item class="text-caption text-grey text-center">
                    &copy;{{ new Date().getFullYear() }}&nbsp;AV&nbsp;Venture&nbsp;Building
                    <br />
                    Wersja: {{ general_store.APP_VERSION }}
                </v-list-item>
            </v-list>
        </v-navigation-drawer>

        <!-- App Bar -->
        <v-app-bar
            v-if="is_logged_in"
            clipped-left
            app
            elevation="0"
            color="background"
            class="dashboard-layout__appbar"
        >
            <v-app-bar-nav-icon
                :icon="drawer ? 'mdi-arrow-collapse-left' : 'mdi-menu'"
                @click="toggleDrawer"
                color="grey"
            />

            <v-spacer />

            <template v-if="auth_store.user">
                <div
                    style="max-width: 240px"
                    class="text-truncate"
                >
                    <UserTile
                        :user="auth_store.user"
                        :self-label="false"
                        subtitle-text="Twoje konto"
                        avatar-size="36"
                        :to="{ name: 'dashboard-my-account' }"
                    />
                </div>
                <v-btn
                    size="small"
                    color="grey"
                    icon="mdi-logout"
                    title="Wyloguj się z aplikacji"
                    @click="logUserOut"
                    class="ml-2"
                ></v-btn>
            </template>
        </v-app-bar>

        <!-- Main Content -->
        <v-main>
            <v-container fluid>
                <slot />
            </v-container>
        </v-main>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";

import { useAuthStore } from "@/store/auth.store";
import { useGeneralStore } from "@/store/general.store";

import UserTile from "@/shared/components/user-tile/UserTile.vue";

import { logUserOut } from "@/services/auth.service";

import { useTypedRouter } from "@/composables/typed-router.composable";
import { UserPermission } from "@/shared/types/user";
import { NavItemChild, NavItemGroup, NavItemLink } from "./dashboard-layout.types";

/*###########
### HOOKS ###
###########*/
const route = useRoute();
const auth_store = useAuthStore();
const typed_router = useTypedRouter();
const general_store = useGeneralStore();

/*###########
### SETUP ###
###########*/
const drawer = ref<boolean>(true);

const items: (NavItemLink | NavItemGroup)[] = [
    {
        type: "link",
        icon: "mdi-view-dashboard",
        title: "Dashboard",
        to: { name: "dashboard" },
        required_permissions: ["users.read"]
    },
    {
        type: "group",
        title: "System",
        children: [
            {
                icon: "mdi-account-group",
                title: "Użytkownicy",
                to: { name: "dashboard-users-list" },
                required_permissions: ["users.read"]
            },
            {
                icon: "mdi-connection",
                title: "Invoicing Platform Connectors",
                to: { name: "dashboard-invoicing-platform-connectors" },
                required_permissions: ["invoicing_platform_connectors.read"]
            }
        ]
    },
    {
        type: "group",
        title: "API",
        children: [
            {
                icon: "mdi-api",
                title: "Klienci API",
                to: { name: "dashboard-api-clients" },
                required_permissions: ["api_clients.read"]
            }
        ]
    },
    {
        type: "group",
        title: "Faktury",
        children: [
            {
                icon: "mdi-invoice-list",
                title: "Wystawione faktury",
                to: { name: "dashboard-invoice-intents" },
                required_permissions: ["invoice_intents.read"]
            }
        ]
    }
];

const filtered_nav_items = computed(() => {
    const FI = [];

    for (let i = 0; i < items.length; i++) {
        const value = items[i];
        if (value.type === "link") {
            if (
                auth_store.userHasPermissions(
                    (value.required_permissions || []) as UserPermission[]
                )
            ) {
                FI.push(value);
            }
        } else if (value.type === "group") {
            const PG = {
                ...value,
                children: [] as NavItemChild[]
            };

            if (Array.isArray(value.children) && value.children) {
                for (let j = 0; j < value.children.length; j++) {
                    const child = value.children[j];
                    if (
                        auth_store.userHasPermissions(
                            (child.required_permissions as UserPermission[]) || []
                        )
                    ) {
                        PG.children.push(child);
                    }
                }
            }

            if (PG.children.length > 0) {
                FI.push(PG);
            }
        }
    }

    return FI;
});

const is_logged_in = computed(() => auth_store.is_logged_in);

const toggleDrawer = () => {
    drawer.value = !drawer.value;
};

const isActive = (targetName: string) => {
    const route_path_map: Record<string, string> = {
        dashboard: "/"
    };

    const expectedPrefix = route_path_map[targetName];
    return route.path.startsWith(expectedPrefix);
};

onMounted(() => {
    if (window.innerWidth < 600) {
        drawer.value = false;
    }
});
</script>
